import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import {
  ControlledField,
  DEFAULT_DRIVER_LANGUAGE,
  DateTimePickerInputField,
  RideOfferType,
  getDateTimeShortFormatWithLocale,
  LanguageField,
  PassengerCountField,
  StartAndEndLocationField,
  DEFAULT_MAX_ADVANCE_TIME,
  DEFAULT_RIDE_DURATION,
  DEFAULT_MAX_RANGE_PASSENGER_COUNT,
} from 'remote/global';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useMapsGoogleConfigurationQuery } from 'api/queries/config';
// eslint-disable-next-line import/no-cycle
import { RideOffersFormSubmitButton, TravelTypeTabsContainer } from '../CreateRideOffersForm';
import { RideDetailsSchemaType, RoundTripRideSchemaType, roundTripRideSchema } from '../../types';
import VehicleSelectField from '../fields/VehicleSelectField';

function VehicleRoundTripRideStepForm({
  defaultValues,
  onValid,
  setFormResult,
}: {
  defaultValues?: Partial<RideDetailsSchemaType>;
  onValid: (data: RoundTripRideSchemaType) => void;
  setFormResult: React.Dispatch<React.SetStateAction<Partial<RideDetailsSchemaType> | undefined>>;
}) {
  const { data: googleConfig } = useMapsGoogleConfigurationQuery();
  const tInstance = useTranslation();
  const { i18n } = tInstance;
  const maxAdvanceTime = dayjs(DEFAULT_MAX_ADVANCE_TIME);
  const formInstance = useForm<RoundTripRideSchemaType>({
    defaultValues: {
      startLocation: undefined,
      endLocation: undefined,
      startTime: '',
      returnTime: '',
      passengerCount: '' as any,
      maxPassengerCount: DEFAULT_MAX_RANGE_PASSENGER_COUNT,
      driverLanguage: DEFAULT_DRIVER_LANGUAGE,
      licensePlate: '',
      clientId: '' as any,
      ...defaultValues,
      type: RideOfferType.ROUND_TRIP,
    },
    resolver: zodResolver(roundTripRideSchema),
  });
  const {
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;
  const startTime = watch('startTime');

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid item container gap={2}>
        <TravelTypeTabsContainer
          type={RideOfferType.ROUND_TRIP}
          setFormResult={setFormResult}
          getValues={getValues}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <StartAndEndLocationField form={formInstance} googleConfig={googleConfig!} />
            <ControlledField
              control={control}
              name='startTime'
              render={(renderProps) => (
                <DateTimePickerInputField
                  {...renderProps}
                  fieldProps={{
                    disablePast: true,
                    maxDateTime: maxAdvanceTime,
                    format: getDateTimeShortFormatWithLocale(i18n.language),
                  }}
                />
              )}
            />
            <ControlledField
              control={control}
              name='returnTime'
              render={(renderProps) => {
                const minDateTime = startTime ? dayjs(startTime) : dayjs();
                let maxDateTime = maxAdvanceTime;
                if (startTime) {
                  const date = dayjs(startTime)
                    .add(DEFAULT_RIDE_DURATION.value, DEFAULT_RIDE_DURATION.unit)
                    .endOf('day');
                  if (dayjs(maxAdvanceTime) >= date) maxDateTime = date;
                }
                return (
                  <DateTimePickerInputField
                    {...renderProps}
                    fieldProps={{
                      disablePast: true,
                      minDateTime,
                      maxDateTime,
                      format: getDateTimeShortFormatWithLocale(i18n.language),
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PassengerCountField form={formInstance} />
            <VehicleSelectField form={formInstance} />
            <LanguageField form={formInstance} name='driverLanguage' />
          </Grid>
        </Grid>
        <RideOffersFormSubmitButton
          isSubmitting={isSubmitting}
          isValidating={isValidating}
          isLoading={isLoading}
        />
      </Grid>
    </form>
  );
}

export default VehicleRoundTripRideStepForm;
